import { lazy } from 'react';

/**
 * Route Configuration — Central registry for all app routes.
 *
 * Each route has:
 *   path     — URL path pattern
 *   element  — Lazy-loaded React component
 *   minRole  — (optional) Minimum role required for access
 *   label    — (optional) Human-readable name for breadcrumbs
 */

// ═══════════════════════════════════════════
//  AUTH PAGES (rendered inside AuthLayout)
// ═══════════════════════════════════════════
const Login = lazy(() => import('@components/auth/Login'));
const Register = lazy(() => import('@components/auth/Register'));
const ForgotPassword = lazy(() => import('@components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@components/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('@components/auth/VerifyEmail'));
const OAuthCallback = lazy(() => import('@components/auth/OAuthCallback'));
const AcceptInvite = lazy(() => import('@components/auth/AcceptInvite'));
const AccountRecovery = lazy(() => import('@components/auth/AccountRecovery'));

// ═══════════════════════════════════════════
//  MAIN APP PAGES (rendered inside MainLayout)
// ═══════════════════════════════════════════

// Dashboard
const Dashboard = lazy(() => import('@components/dashboard/Dashboard'));

// Unified Settings (Workspace + Admin + Account)
const SettingsPage = lazy(() => import('@components/settings/SettingsPage'));
const SubscriptionPage = lazy(() => import('@components/subscription/SubscriptionPage'));
const RBACPage = lazy(() => import('@components/rbac/RBACPage'));
const MyAccessPage = lazy(() => import('@components/settings/MyAccessPage'));
const UsersPage = lazy(() => import('@components/users/UsersPage'));
const ProjectSettingsPage = lazy(() => import('@components/projects/ProjectSettingsPage'));

// Component showcase (dev only)
const ComponentShowcase = lazy(() => import('@components/showcase/ComponentShowcase'));

// ── Future module placeholders (lazy-loaded when built) ──

// Projects (Step 8)
const ProjectList = lazy(() => import('@components/projects/ProjectList'));
const ProjectView = lazy(() => import('@components/projects/ProjectView'));
const AddProject = lazy(() => import('@components/projects/AddProject'));

// Tasks (Step 9)
const TaskList = lazy(() => import('@components/tasks/TaskList'));
const TaskView = lazy(() => import('@components/tasks/TaskView'));
const DependencyGraphView = lazy(() => import('@components/tasks/DependencyGraphView'));
const TaskAutomationPage = lazy(() => import('@components/tasks/TaskAutomationPage'));
const TaskApprovalsPage = lazy(() => import('@components/tasks/TaskApprovalsPage'));

// Sprints (Step 13)
const SprintBoard = lazy(() => import('@components/sprints/SprintPage'));

// Kanban (Step 12)
const KanbanBoard = lazy(() => import('@components/tasks/KanbanPage'));

// Gantt (Step 14)
const GanttChart = lazy(() => import('@components/gantt/GanttPage'));

// Milestones (Step 15)
const MilestoneRoadmap = lazy(() => import('@components/milestones/MilestonePage'));

// HiveLink Chat (Step 20-21)
const HiveLinkChat = lazy(() => import('@components/hivelink/HiveLinkPage'));

/**
 * Routes that are authenticated like the rest of the app but render WITHOUT
 * MainLayout — full-window products with their own chrome.
 */
export const standaloneAppRoutes = [
  { path: '/hivelink/*', element: HiveLinkChat, label: 'HiveLink' },
];

// Timesheets (Step 17)
const TimesheetDashboard = lazy(() => import('@components/timeTracking/TimesheetPage'));

// Budget (Step 18)
const BudgetDashboard = lazy(() => import('@components/budget/BudgetPage'));

// CapacityIQ (Step 19)
const CapacityIQPage = lazy(() => import('@components/resource/CapacityIQPage'));

// Goals (Step 25)
const GoalDashboard = lazy(() => import('@components/goals/GoalPage'));

// Reports / InsightLens (Step 27)
const ReportDashboard = lazy(() => import('@components/reports/ReportPage'));
const QueryBuilderPage = lazy(() => import('@components/reports/QueryBuilderPanel'));
const TemplatesPage   = lazy(() => import('@components/templates/TemplatesPage'));

// Documents (Step 26)
const DocumentHub = lazy(() => import('@components/documents/DocumentPage'));
const SearchResultsPage = lazy(() => import('@components/search/SearchResultsPage'));
const KnowledgeBase = lazy(() => import('@components/knowledgebase/KnowledgeBasePage'));

// Workflows
const WorkflowList = lazy(() => import('@components/workflows/FlowForgePage'));

// Zuno AI
const ZunoPage = lazy(() => import('@components/zuno/ZunoPage'));

// Zuno Studio — AI Agent Builder
const ZunoStudioPage = lazy(() => import('@components/zunoStudio/ZunoStudioPage'));
const AgentBuilder   = lazy(() => import('@components/zunoStudio/AgentBuilder'));
const AgentSpacePage   = lazy(() => import('@components/zunoStudio/AgentSpacePage'));
const AgentConversation = lazy(() => import('@components/zunoStudio/AgentConversation'));
const AgentActionQueue  = lazy(() => import('@components/zunoStudio/AgentActionQueue'));
const ActionLedger      = lazy(() => import('@components/zunoStudio/ActionLedger'));
const AIDiagnosticsPage = lazy(() => import('@components/zunoStudio/AIDiagnosticsPage'));
const AgentEvalPage     = lazy(() => import('@components/zunoStudio/AgentEvalPage'));
const OrchestratePage   = lazy(() => import('@components/zunoStudio/OrchestratePage'));
const AgentPlayground   = lazy(() => import('@components/zunoStudio/AgentPlayground'));
const AgentAnalytics    = lazy(() => import('@components/zunoStudio/AgentAnalytics'));
const AgentTemplates    = lazy(() => import('@components/zunoStudio/AgentTemplates'));
const AgentKnowledgeManager = lazy(() => import('@components/zunoStudio/AgentKnowledgeManager'));
const AgentFlowBuilder = lazy(() => import('@components/zunoStudio/AgentFlowBuilder'));

// Calendar (Step 16)
const CalendarView = lazy(() => import('@components/calendar/CalendarPage'));

const DesktopDownload = lazy(() => import('@components/desktop/DesktopDownloadPage'));
const ClientManage = lazy(() => import('@components/clientPortal/ClientManagePage'));
// LEGACY REMOVED — const InvoicingPage = lazy(() => import('@components/invoicing/InvoicingPage'));
const AccountsPage       = lazy(() => import('@components/accounts/AccountsPage'));
const ApprovalSettingsPage = lazy(() => import('@components/approvals/ApprovalSettingsPage'));
const IntegrationsPage   = lazy(() => import('@components/integrations/IntegrationsPage'));
const ExportPage         = lazy(() => import('@components/export/ExportPage'));
const CompliancePage     = lazy(() => import('@components/compliance/CompliancePage'));

// Employee Management Module (Module 1)
const EmployeeDirectory    = lazy(() => import('@components/employees/EmployeeDirectory'));
const EmployeeDetail       = lazy(() => import('@components/employees/EmployeeDetail'));
const OrgChartPage         = lazy(() => import('@components/employees/OrgChart'));
const TeamsPage            = lazy(() => import('@components/employees/TeamsPage'));
const EmployeeAnalytics    = lazy(() => import('@components/employees/EmployeeAnalytics'));
const EmployeeCustomFields = lazy(() => import('@components/employees/EmployeeCustomFields'));
const OnboardingTemplates  = lazy(() => import('@components/employees/OnboardingTemplates'));
const PerformanceReviewsPage = lazy(() => import('@components/employees/PerformanceReviewsPage'));
const LeaveWorkflowSettings = lazy(() => import('@components/employees/LeaveWorkflowSettings'));
const FeedbackWorkflowSettings = lazy(() => import('@components/employees/FeedbackWorkflowSettings'));
const ModuleWorkflows = lazy(() => import('@components/employees/ModuleWorkflows'));

// Portfolio Management
const PortfolioPage          = lazy(() => import('@components/portfolio/PortfolioPage'));
const PortfolioDetailPage    = lazy(() => import('@components/portfolio/PortfolioDetailPage'));
const PortfolioRoadmapPage   = lazy(() => import('@components/portfolio/PortfolioRoadmapPage'));
const PortfolioResourcesPage = lazy(() => import('@components/portfolio/PortfolioResourcesPage'));
const PortfolioBudgetPage    = lazy(() => import('@components/portfolio/PortfolioBudgetPage'));

// ═══════════════════════════════════════════
//  ROUTE DEFINITIONS
// ═══════════════════════════════════════════

/**
 * Auth routes — rendered inside AuthLayout
 * No authentication required
 */
export const authRoutes = [
  { path: 'login', element: Login },
  { path: 'register', element: Register },
  { path: 'forgot-password', element: ForgotPassword },
  { path: 'reset-password/:token', element: ResetPassword },
  { path: 'verify-email/:token', element: VerifyEmail },
  { path: 'account-recovery', element: AccountRecovery },
  { path: 'account-recovery/:token', element: AccountRecovery },
];

/**
 * Standalone public routes — rendered WITHOUT any layout
 * (OAuth callback, invite acceptance — these handle their own UI)
 */
export const standalonePublicRoutes = [
  { path: '/oauth/callback', element: OAuthCallback },
  { path: '/invite/:token', element: AcceptInvite },
];

/**
 * App routes — rendered inside MainLayout with ProtectedRoute
 * Authentication required, optional role requirements
 *
 * minRole uses hierarchy: client < guest < member < manager < admin < owner
 */
export const appRoutes = [
  // Dashboard
  { path: '/', element: Dashboard, label: 'Dashboard' },

  // Global cross-module search
  { path: 'search', element: SearchResultsPage, label: 'Search' },

  // Workspace / Settings (unified — includes admin panel + account)
  { path: 'settings', element: SettingsPage, label: 'Settings' },
  { path: 'settings/:workspaceId', element: SettingsPage, label: 'Settings' },
  { path: 'settings/access', element: MyAccessPage, label: 'My Access' },
  { path: 'admin', element: SettingsPage, label: 'Settings', minRole: 'admin' },
  { path: 'admin/*', element: SettingsPage, label: 'Settings', minRole: 'admin' },
  { path: 'audit-log', element: SettingsPage, label: 'Settings', minRole: 'admin' },
  { path: 'members', element: UsersPage, label: 'Team Members' },

  // Billing (SC4 — promoted to top-level, previously only accessible via Settings → Billing tab)
  { path: 'billing', element: SubscriptionPage, label: 'Billing', permission: 'settings:billing' },

  // Employee Management (Module 1)
  { path: 'employees', element: EmployeeDirectory, label: 'Employees' },
  { path: 'employees/org-chart', element: OrgChartPage, label: 'Org Chart' },
  { path: 'employees/teams', element: TeamsPage, label: 'Teams' },
  { path: 'employees/analytics', element: EmployeeAnalytics, label: 'Employee Analytics' },
  { path: 'employees/custom-fields', element: EmployeeCustomFields, label: 'Custom Fields', minRole: 'admin' },
  { path: 'employees/onboarding', element: OnboardingTemplates, label: 'Onboarding', minRole: 'manager' },
  { path: 'employees/reviews', element: PerformanceReviewsPage, label: 'Performance Reviews', minRole: 'manager' },
  { path: 'employees/:userId', element: EmployeeDetail, label: 'Employee Detail' },

  // Module Workflows & Configuration
  { path: 'module-workflows', element: ModuleWorkflows, label: 'Module Workflows' },
  { path: 'module-workflows/leave', element: LeaveWorkflowSettings, label: 'Leave Settings', minRole: 'manager' },
  { path: 'module-workflows/feedback', element: FeedbackWorkflowSettings, label: 'Feedback Settings', minRole: 'manager' },
  { path: 'module-workflows/task-automation', element: TaskAutomationPage, label: 'Task Automation', minRole: 'manager' },

  // Projects
  { path: 'projects', element: ProjectList, label: 'Projects' },
  { path: 'projects/new', element: AddProject, label: 'New Project' },
  { path: 'projects/:projectId/settings', element: ProjectSettingsPage, label: 'Project Settings' },
  { path: 'projects/:projectId/*', element: ProjectView, label: 'Project' },

  // Tasks
  { path: 'tasks', element: TaskList, label: 'Tasks' },
  { path: 'tasks/:taskId', element: TaskView, label: 'Task Details' },
  { path: 'dependency-graph', element: DependencyGraphView, label: 'Dependency Graph' },
  { path: 'task-approvals', element: TaskApprovalsPage, label: 'Task Approvals' },

  // Sprints
  { path: 'sprints', element: SprintBoard, label: 'Sprints' },

  // Views
  { path: 'kanban', element: KanbanBoard, label: 'Kanban Board' },
  { path: 'gantt', element: GanttChart, label: 'Gantt Chart' },
  { path: 'milestones', element: MilestoneRoadmap, label: 'Milestones' },
  { path: 'calendar', element: CalendarView, label: 'Calendar' },

  // Collaborate
  // HiveLink is NOT in appRoutes any more. It opens in its own tab with its
  // own shell — no Zentrix sidebar, no Zentrix topbar — so it must not be
  // wrapped in MainLayout. See standaloneAppRoutes below and AppRouter.

  { path: 'documents', element: DocumentHub, label: 'Documents' },
  { path: 'knowledge-base', element: KnowledgeBase, label: 'Knowledge Base' },

  // Track
  { path: 'timesheets', element: TimesheetDashboard, label: 'Timesheets' },
  { path: 'budget', element: BudgetDashboard, label: 'Budget' },
  { path: 'goals', element: GoalDashboard, label: 'Goals' },
  { path: 'reports',    element: ReportDashboard, label: 'Reports' },
  { path: 'reports/query-builder', element: QueryBuilderPage, label: 'Query Builder' },
  { path: 'templates',  element: TemplatesPage,   label: 'Templates' },
  { path: 'capacity', element: CapacityIQPage, label: 'CapacityIQ' },

  // Manage
  { path: 'workflows', element: WorkflowList, label: 'Workflows', minRole: 'admin' },
  { path: 'zuno', element: ZunoPage, label: 'Zuno AI' },

  // Zuno Studio — AI Agent Builder
  { path: 'studio',                    element: ZunoStudioPage, label: 'Zuno Studio' },
  { path: 'studio/agents/new',         element: AgentBuilder,   label: 'Create Agent' },
  { path: 'studio/agents/:agentId/edit', element: AgentBuilder, label: 'Edit Agent' },
  { path: 'studio/agents/:agentId/playground', element: AgentPlayground, label: 'Agent Playground' },
  { path: 'studio/agents/:agentId/knowledge', element: AgentKnowledgeManager, label: 'Agent Knowledge' },
  { path: 'studio/agents/:agentId/flow', element: AgentFlowBuilder, label: 'Flow Builder' },
  { path: 'studio/templates',          element: AgentTemplates, label: 'Agent Templates' },
  { path: 'studio/analytics',          element: AgentAnalytics, label: 'Studio Analytics' },
  { path: 'studio/actions',            element: AgentActionQueue, label: 'Action Queue' },
  { path: 'studio/ledger',             element: ActionLedger,     label: 'Action Ledger' },
  { path: 'studio/evals',              element: AgentEvalPage,    label: 'Agent Evaluations' },
  { path: 'studio/orchestrate',        element: OrchestratePage,  label: 'Ask Across Agents' },
  { path: 'studio/diagnostics',        element: AIDiagnosticsPage, label: 'AI Diagnostics' },
  { path: 'agent-space',               element: AgentSpacePage,   label: 'Agent Space' },
  { path: 'agent-space/:agentId/chat', element: AgentConversation, label: 'Agent Chat' },
  { path: 'agent-space/:agentId/chat/:convId', element: AgentConversation, label: 'Agent Chat' },

  { path: 'rbac', element: RBACPage, label: 'Access Control', minRole: 'admin' },

  // Portfolio Management
  { path: 'portfolio',            element: PortfolioPage,          label: 'Portfolio' },
  { path: 'portfolio/roadmap',    element: PortfolioRoadmapPage,   label: 'Cross-Project Roadmap' },
  { path: 'portfolio/resources',  element: PortfolioResourcesPage, label: 'Resource Portfolio' },
  { path: 'portfolio/budget',     element: PortfolioBudgetPage,    label: 'Budget Portfolio' },
  { path: 'portfolio/:portfolioId', element: PortfolioDetailPage,  label: 'Portfolio' },

  // Dev tools
  { path: 'download', element: DesktopDownload, label: 'Desktop App' },
  { path: 'client-manage', element: ClientManage, label: 'Client Portal', minRole: 'manager' },
  // LEGACY REMOVED — { path: 'invoicing', element: InvoicingPage, label: 'Invoicing', minRole: 'manager' },
  { path: 'accounts', element: AccountsPage, label: 'Accounts & Sales', minRole: 'manager' },
  { path: 'approvals', element: ApprovalSettingsPage, label: 'Approvals' },
  { path: 'integrations', element: IntegrationsPage, label: 'Integrations', minRole: 'admin' },
  { path: 'export', element: ExportPage, label: 'Data Export' },
  { path: 'compliance', element: CompliancePage, label: 'Compliance', minRole: 'admin' },

  // Dev tools
  { path: 'ui', element: ComponentShowcase, label: 'Components' },
];